/**
 * C-Search — the load search consumer (ADR-040), one page.
 *
 * The second consumer of the observations topic, so its own sidebar entry, set up the same way as C-Market Intel:
 * routed tabs (`/load-pipeline/search/:tab`), only the active one mounted.
 *
 * PROBE asks the new search a question and shows what it answers. COMPARE asks the legacy unified search the same
 * question and shows where they disagree — the evidence the shadow phase (ADR-040 §C P2) needs before any client
 * moves. Both are reads: nothing here writes to the store, meters a quota or calls a live loadboard.
 */

import Box from '@mui/joy/Box'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import Tabs from '@mui/joy/Tabs'
import Typography from '@mui/joy/Typography'
import { useNavigate, useParams } from 'react-router-dom'
import SearchComparePage from './SearchComparePage'
import SearchLivePage from './SearchLivePage'
import SearchProbePage from './SearchProbePage'

const TABS = [
    { key: 'probe', label: 'Probe', render: () => <SearchProbePage /> },
    { key: 'compare', label: 'Compare with legacy', render: () => <SearchComparePage /> },
    { key: 'live', label: 'Live searches', render: () => <SearchLivePage /> },
] as const

const DEFAULT_TAB = TABS[0].key

export default function LoadPipelineSearchPage() {
    const { tab } = useParams<{ tab?: string }>()
    const navigate = useNavigate()
    const active = TABS.some((t) => t.key === tab) ? (tab as string) : DEFAULT_TAB

    return (
        <Box sx={{ p: 3 }}>
            <Typography level="h2" sx={{ mb: 0.5 }}>
                C-Search
            </Typography>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                The search indexer reads <code>loadpipe-observations-search-sub</code> into the search store; <code>load-search-service</code> answers searches
                from it.
            </Typography>

            <Tabs value={active} onChange={(_, value) => navigate(`/load-pipeline/search/${value}`)} sx={{ bgcolor: 'transparent' }}>
                <TabList tabFlex="none" sx={{ mb: 1 }}>
                    {TABS.map((t) => (
                        <Tab key={t.key} value={t.key} variant="plain" color="neutral">
                            {t.label}
                        </Tab>
                    ))}
                </TabList>
            </Tabs>

            {TABS.find((t) => t.key === active)?.render()}
        </Box>
    )
}
